/**
 * R3b signing cockpit — control + status surface.
 *
 * Lives in the editor's workflow row alongside ReviewPanel. The view is
 * driven entirely by `contracts.contract_status`, and each phase is made
 * to read at a glance (accent rail + status pill, live ping while
 * in-flight):
 *
 *   - `draft` / `pending`:
 *       pre-flight (signing parties + emails) + signer roster + deadline
 *       & reminder settings + "Skicka för signering". Pre-flight failure
 *       surfaces structured errors so the user knows what's missing.
 *   - `sent` / `viewed` / `awaiting_signature`:
 *       a signer tracker (waiting → öppnat → signerat), deadline /
 *       reminder metadata, and actions (remind / prolong / cancel).
 *       Polls `signing.poll_state` every 5s with backoff — the webhook is
 *       the primary update path in prod (ADR-0024); polling is the dev /
 *       belt-and-braces fallback and the active reconciler (ADR-0032 v4).
 *   - `signed`:
 *       a sealed-completion summary + per-signer timestamps + a pointer
 *       to the final PDF.
 *   - `cancelled`:
 *       the resend form, flagged with the prior attempt.
 *
 * No `/sign/:token` page exists on our side — Scrive hosts the signing
 * UX entirely. We only orchestrate.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  BellRing,
  CalendarClock,
  Check,
  Clock,
  Download,
  Eye,
  FileText,
  Hourglass,
  Send,
  ShieldCheck,
  Upload,
  X,
} from 'lucide-react';
import type { BankIdOrderState, IrDiagnostic, SigningMethod } from '../api.js';
import {
  tenantApi,
  type ContractDetail,
  type ContractParty,
  type ScrivePollStateSigner,
  type SignerType,
} from '../api.js';
import {
  getTemplateVersion,
  isSigningParty,
  templateDocumentParts,
} from '@hyranai/regta-templates';
import { useInvalidate, useTenantQuery } from '../query.js';
import { ScriveActivityTimeline } from './ScriveActivityTimeline.js';
import { irDiagnosticsOf, signingErrorText } from '../lib/ir-errors.js';
import { IrSigningDiagnostics } from './IrSigningDiagnostics.js';
import { SignerRosterEditor, type RosterPayload } from './SignerRosterEditor.js';
import { SigningModeBadge, type SigningMode } from './SigningModeBadge.js';
import { useSigningDraft } from './SigningPlanPanel.js';

type SigningStatus = ContractDetail['status'];

/** Active signing states where ongoing polling is worthwhile. */
const POLLING_STATUSES: ReadonlySet<SigningStatus> = new Set([
  'sent',
  'viewed',
  'awaiting_signature',
]);

/**
 * Pick a refresh interval based on time since the last observed activity.
 * Most state transitions happen within the first minute (delivery + first
 * view); after that the long-tail is "waiting for the last signer" where
 * a 30s tick is plenty. Returns 0 to disable polling.
 */
function pollIntervalMs(sinceActivityMs: number | null): number {
  if (sinceActivityMs == null) return 5000;
  if (sinceActivityMs < 60_000) return 5000;
  if (sinceActivityMs < 600_000) return 15_000;
  return 30_000;
}

/**
 * Track `document.visibilityState`. Polling pauses when the tab is
 * hidden — long-lived signing flows otherwise burn ~12 Scrive calls/min
 * per backgrounded tab. SWR resumes the next tick on focus.
 */
function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(() =>
    typeof document === 'undefined' ? true : !document.hidden,
  );
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onChange = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

/** In-flight sub-status label (shown inside the indigo pill). */
const INFLIGHT_LABEL: Partial<Record<SigningStatus, string>> = {
  sent: 'Skickat',
  viewed: 'Öppnat',
  awaiting_signature: 'Sista signering',
  awaiting_manual_signature: 'Väntar på underskrift',
};


/** Manual reminders are capped at one per 12h (mirrors the backend gate);
 *  the button disables until then. */
const REMINDER_MIN_INTERVAL_MS = 12 * 60 * 60 * 1000;

type MissingSigner = {
  signer_id?: string;
  party_id: string;
  role: string;
  ordinal: number;
  name: string;
};

/** Mirror of the backend's BankID personnummer gate: 10 or 12 digits. */
function looksLikePersonnummer(v: string | null | undefined): boolean {
  if (!v) return false;
  const digits = v.replace(/[^0-9]/g, '');
  return digits.length === 10 || digits.length === 12;
}

/** A signer as shown in the tracker — one row per signer (per ADR-0044),
 *  sourced from poll_state's per-signer array. */
type SignerView = {
  id: string;
  name: string;
  email: string | null;
  role: string;
  capacity: string | null;
  seenAt: string | null;
  signedAt: string | null;
};

type SignerState = 'signed' | 'viewed' | 'waiting';

function signerState(s: SignerView): SignerState {
  if (s.signedAt) return 'signed';
  if (s.seenAt) return 'viewed';
  return 'waiting';
}

function signerCapacityLabel(t: SignerType | null): string | null {
  switch (t) {
    case 'firmatecknare':
      return 'Firmatecknare';
    case 'vd':
      return 'VD';
    case 'ombud':
      return 'Ombud';
    default:
      return null;
  }
}

type PreflightError =
  | { reason: 'no_signing_parties' }
  | {
      reason: 'missing_party_emails';
      missing: Array<MissingSigner>;
    }
  | {
      reason: 'missing_signer_personal_number';
      missing: Array<MissingSigner>;
    }
  | { reason: 'scrive_account_not_provisioned' }
  | {
      reason: 'scrive_bankid_not_active';
      orderState: BankIdOrderState;
      orderedAt: string | null;
    }
  | { reason: 'ir_document_has_errors'; message: string; diagnostics: IrDiagnostic[] }
  /** ADR-0102 decision 8: adapted text needs a current approved granskning. */
  | { reason: 'review_required_for_deviation'; message: string }
  | { reason: 'other'; message: string };

export function SigningPanel({
  token,
  contract,
  signingMode = 'scrive',
}: {
  token: string;
  contract: ContractDetail;
  signingMode?: SigningMode;
}) {
  const invalidate = useInvalidate();
  const status = contract.status;

  // Polling refresh while signing is active. Calls `signing.poll_state`
  // which actively reconciles state from Scrive (per ADR-0032 v4 §
  // "Polling reconciler") — webhook becomes an acceleration, not a
  // load-bearing dependency. Works in local dev where Scrive can't
  // reach localhost.
  //
  // Status handling:
  //   - sent/viewed/awaiting_signature: ongoing poll with backoff.
  //   - signed: one-shot reconcile on mount (refreshInterval=0) so any
  //     party timestamps missed by a webhook gap are healed. Polling
  //     can't observe sealed_at (that's webhook-exclusive — see
  //     scrive-reconcile.ts), so no ongoing tick is useful.
  //   - everything else: no fetch (key disabled), fall back to the
  //     static contracts.get + contract_parties.list reads.
  // `export_only` contracts have no signing attempt to reconcile, so never
  // poll Scrive for them (their `sent` status would otherwise match
  // POLLING_STATUSES). They render the dedicated ExportOnlyPanel below.
  const isExportOnly = signingMode === 'export_only';
  const isActive = !isExportOnly && POLLING_STATUSES.has(status);
  const isOneShot = !isExportOnly && status === 'signed';
  const visible = useDocumentVisible();
  // SWR's function-form refreshInterval is evaluated after every fetch,
  // so the backoff curve reacts to the freshest poll response. Prefer
  // the server's `last_callback_at` (updated on every reconcile) over
  // the static `contract.updatedAt`. Return 0 to stop polling.
  const pollQ = useTenantQuery(
    'signing.poll_state',
    (cid) => tenantApi.signing.poll_state({ contract_id: cid as string }),
    [contract.id],
    {
      ignoreActiveGroup: true,
      enabled: isActive || isOneShot,
      refreshInterval: (latest) => {
        if (!isActive || !visible) return 0;
        const iso =
          latest?.contract.last_callback_at ?? contract.updatedAt;
        const since = iso ? Date.now() - new Date(iso).getTime() : null;
        return pollIntervalMs(since);
      },
      revalidateOnFocus: false,
    },
  );
  const partiesQ = useTenantQuery(
    'contract_parties.list',
    (cid) => tenantApi.contract_parties.list({ contract_id: cid as string }),
    [contract.id],
    {
      ignoreActiveGroup: true,
      revalidateOnFocus: false,
    },
  );

  // Source of truth for party structure is `contract_parties.list`.
  // While polling, overlay the freshest `seen_at` / `signed_at` from
  // the poll response so the UI ticks forward as parties view/sign
  // without waiting for the next contract_parties.list refresh.
  const baseParties: ContractParty[] = partiesQ.data?.parties ?? [];
  const polledById = new Map<string, { signed_at: string | null; seen_at: string | null }>(
    (isActive && pollQ.data ? pollQ.data.parties : []).map((p) => [
      p.id,
      { signed_at: p.signed_at, seen_at: p.seen_at },
    ]),
  );
  const parties: ContractParty[] = baseParties.map((p) => {
    const overlay = polledById.get(p.id);
    if (!overlay) return p;
    return { ...p, signedAt: overlay.signed_at ?? p.signedAt };
  });
  const signingMeta = getTemplateVersion(contract.templateKey, contract.templateVersion)?.metadata;
  const signers = parties.filter((p) => isSigningParty(signingMeta, p.role, p.ordinal));

  // Per-signer view for the in-flight / completed views (ADR-0044): a
  // party can have several signers, so render poll_state's per-signer
  // array — not the parties. Fall back to one-row-per-party only for
  // legacy contracts that have no signer rows.
  const partyRoleById = new Map(parties.map((p) => [p.id, p.role]));
  const pollSigners: ScrivePollStateSigner[] =
    isActive || isOneShot ? pollQ.data?.signers ?? [] : [];
  const signerViews: SignerView[] = pollSigners.length
    ? pollSigners.map((s) => ({
        id: s.id,
        name: s.legal_name,
        email: s.email,
        role: partyRoleById.get(s.party_id) ?? '',
        capacity: signerCapacityLabel(s.signer_type),
        seenAt: s.seen_at,
        signedAt: s.signed_at,
      }))
    : signers.map((p) => ({
        id: p.id,
        name: p.legalName,
        email: p.email,
        role: p.role,
        capacity: null,
        seenAt: null,
        signedAt: p.signedAt ?? null,
      }));

  const onChanged = useCallback(async () => {
    await Promise.all([
      invalidate('contracts.get', contract.id),
      invalidate('contracts.list'),
      invalidate('contract_parties.list', contract.id),
      invalidate('signing.poll_state', contract.id),
    ]);
  }, [invalidate, contract.id]);

  // Don't render the panel at all on draft and in_review. Those
  // states have their own controls (StatusBar / ReviewPanel) and
  // showing a "Send for signing" message there would conflict
  // with the review flow.
  if (status === 'draft' || status === 'in_review') return null;

  // `export_only` templates carry no signature and run no signing flow —
  // the user exports the final PDF and sends it out-of-band. A dedicated
  // surface replaces the Scrive/manual cockpit entirely.
  if (isExportOnly) {
    return <ExportOnlyPanel contract={contract} onChanged={onChanged} />;
  }

  const activeMethod = contract.activeSigning?.method ?? null;
  const isScriveInflight =
    status === 'sent' || status === 'viewed' || status === 'awaiting_signature';
  const isManualInflight = status === 'awaiting_manual_signature';
  const phase: Phase =
    status === 'signed'
      ? 'signed'
      : status === 'cancelled'
        ? 'cancelled'
        : isScriveInflight || isManualInflight
          ? 'inflight'
          : 'ready';

  return (
    <PanelShell phase={phase} status={status} signingMode={signingMode}>
      {status === 'cancelled' && (
        <ReturnToDraftAction contractId={contract.id} onChanged={onChanged} />
      )}
      {(status === 'pending' || status === 'cancelled') && (
        <SigningMethodGate
          token={token}
          contractId={contract.id}
          signers={signers}
          onChanged={onChanged}
          resend={status === 'cancelled'}
          lastScriveDocumentId={contract.activeSigning?.scriveDocumentId ?? null}
          parent={contract.parent}
        />
      )}

      {isScriveInflight && (
        <InFlight
          contract={contract}
          signers={signerViews}
          signingInfo={{
            dueAt: pollQ.data?.contract.signing_due_at ?? null,
            autoRemindAt: pollQ.data?.contract.auto_remind_at ?? null,
            lastReminderSentAt: pollQ.data?.contract.last_reminder_sent_at ?? null,
          }}
          onCancelled={onChanged}
        />
      )}

      {isManualInflight && (
        <ManualInFlight contract={contract} signers={signerViews} onChanged={onChanged} />
      )}

      {status === 'signed' &&
        (activeMethod === 'manual' ? (
          <ManualCompleted contract={contract} signers={signerViews} />
        ) : (
          <Completed contract={contract} signers={signerViews} />
        ))}

      {/* Activity timeline — full Scrive lifecycle once a contract has been
          sent. Manual contracts use the ManualInFlight/Completed views. */}
      {activeMethod !== 'manual' && (
        <ScriveActivityTimeline token={token} contract={contract} />
      )}
    </PanelShell>
  );
}

// ─── Export-only (no signing) ────────────────────────────────────────

/**
 * Signing surface for `export_only` templates — no Scrive, no manual
 * upload, no signature. The user exports the final PDF (clean render) and
 * marks the contract as sent (it's distributed out-of-band, e.g. by
 * registered post). Two states:
 *   - `pending` / `cancelled`: preview + "Markera som skickad".
 *   - `sent` (and any later terminal state): a sent confirmation + the
 *     final PDF to download.
 */
function ExportOnlyPanel({
  contract,
  onChanged,
}: {
  contract: ContractDetail;
  onChanged: () => Promise<void>;
}) {
  const status = contract.status;
  const isSent =
    status === 'sent' ||
    status === 'viewed' ||
    status === 'awaiting_signature' ||
    status === 'signed' ||
    status === 'archived';
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [irDiagnostics, setIrDiagnostics] = useState<IrDiagnostic[]>([]);

  const openPdf = async () => {
    try {
      const blob = await tenantApi.contracts.render(contract.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setErr((e as { message?: string }).message ?? 'Kunde inte öppna PDF:en');
    }
  };

  const markSent = async () => {
    setBusy(true);
    setErr(null);
    setIrDiagnostics([]);
    try {
      await tenantApi.signing.mark_exported({ contract_id: contract.id });
      await onChanged();
    } catch (e) {
      setErr(signingErrorText(e, 'Kunde inte markera som skickad'));
      setIrDiagnostics(irDiagnosticsOf(e));
    } finally {
      setBusy(false);
    }
  };

  const rail = isSent
    ? 'shadow-[inset_3px_0_0_0_var(--color-emerald-500)]'
    : 'shadow-[inset_3px_0_0_0_var(--color-sky-400)]';
  const pill = isSent
    ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
    : 'border-sky-300 bg-sky-50 text-sky-700';

  return (
    <section
      className={`flex flex-col gap-4 rounded-lg border border-stone-200 bg-white p-4 ${rail}`}
    >
      <header className="flex items-center justify-between gap-3">
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-400">
          Export
        </span>
        <div className="flex items-center gap-2">
          <SigningModeBadge signingMode="export_only" />
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${pill}`}
          >
            {isSent ? <Check className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
            {isSent ? 'Skickat' : 'Redo att skicka'}
          </span>
        </div>
      </header>

      {isSent ? (
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50/70 px-3 py-2.5">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            <div className="text-xs text-emerald-900">
              <div className="font-medium">Markerat som skickat.</div>
              <div className="mt-0.5 text-emerald-800/80">
                Dokumentet signeras inte. Ladda ner PDF:en och skicka den med
                rekommenderad post till hyresgästen.
              </div>
            </div>
          </div>
          <div className="border-t border-stone-100 pt-3">
            <DownloadBtn onClick={() => void openPdf()}>Ladda ner PDF</DownloadBtn>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-[11px] leading-relaxed text-stone-600">
            Det här dokumentet signeras inte. Granska och exportera PDF:en, och
            markera den sedan som skickad. Skicka dokumentet med rekommenderad
            post till hyresgästen.
          </p>
          {err && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              {err}
            </p>
          )}
          <IrSigningDiagnostics diagnostics={irDiagnostics} />
          <div className="flex flex-wrap items-center gap-2 border-t border-stone-100 pt-3">
            <DownloadBtn onClick={() => void openPdf()}>Förhandsgranska PDF</DownloadBtn>
            <button
              type="button"
              onClick={() => void markSent()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-sky-700 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-sky-800 disabled:opacity-50"
            >
              <Send className="h-3.5 w-3.5" />
              {busy ? 'Markerar…' : 'Markera som skickad'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ─── Phase shell ─────────────────────────────────────────────────────

type Phase = 'ready' | 'inflight' | 'signed' | 'cancelled';

const PHASE: Record<
  Phase,
  { rail: string; pill: string; icon: ReactNode; label: string; live?: boolean }
> = {
  ready: {
    rail: 'shadow-[inset_3px_0_0_0_var(--color-stone-400)]',
    pill: 'border-stone-300 bg-stone-50 text-stone-600',
    icon: <Send className="h-3 w-3" />,
    label: 'Redo att skicka',
  },
  inflight: {
    rail: 'shadow-[inset_3px_0_0_0_var(--color-indigo-500)]',
    pill: 'border-indigo-300 bg-indigo-50 text-indigo-700',
    icon: <Hourglass className="h-3 w-3" />,
    label: 'Pågår',
    live: true,
  },
  signed: {
    rail: 'shadow-[inset_3px_0_0_0_var(--color-emerald-500)]',
    pill: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    icon: <ShieldCheck className="h-3 w-3" />,
    label: 'Signerat',
  },
  cancelled: {
    rail: 'shadow-[inset_3px_0_0_0_var(--color-red-400)]',
    pill: 'border-red-300 bg-red-50 text-red-700',
    icon: <X className="h-3 w-3" />,
    label: 'Avbrutet',
  },
};

function PanelShell({
  phase,
  status,
  signingMode,
  children,
}: {
  phase: Phase;
  status: SigningStatus;
  signingMode: SigningMode;
  children: ReactNode;
}) {
  const p = PHASE[phase];
  const pillLabel =
    phase === 'inflight' ? INFLIGHT_LABEL[status] ?? p.label : p.label;
  return (
    <section
      className={`flex flex-col gap-4 rounded-lg border border-stone-200 bg-white p-4 ${p.rail}`}
    >
      <header className="flex items-center justify-between gap-3">
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-400">
          Signering
        </span>
        <div className="flex items-center gap-2">
          <SigningModeBadge signingMode={signingMode} />
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${p.pill}`}
          >
            {p.live ? (
              <span className="relative flex h-2 w-2 items-center justify-center">
                <span className="regta-ping absolute h-2 w-2 rounded-full bg-indigo-500" />
                <span className="regta-breathe relative h-1.5 w-1.5 rounded-full bg-indigo-500" />
              </span>
            ) : (
              p.icon
            )}
            {pillLabel}
          </span>
        </div>
      </header>
      {children}
    </section>
  );
}

// ─── Signer tracker ──────────────────────────────────────────────────

const NODE_STYLE: Record<SignerState, string> = {
  signed: 'border-emerald-500 bg-emerald-500 text-white',
  viewed: 'border-indigo-400 bg-white text-indigo-600',
  waiting: 'border-stone-200 bg-stone-50 text-stone-400',
};

const STATE_TEXT: Record<SignerState, { label: string; cls: string }> = {
  signed: { label: 'Signerat', cls: 'text-emerald-700' },
  viewed: { label: 'Öppnat', cls: 'text-indigo-600' },
  waiting: { label: 'Väntar', cls: 'text-stone-400' },
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '–';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function SignerTracker({ signers }: { signers: SignerView[] }) {
  const total = signers.length;
  const signed = signers.filter((s) => s.signedAt).length;
  const pct = total > 0 ? Math.round((signed / total) * 100) : 0;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-3">
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-400">
          Undertecknare
        </span>
        <div className="h-px flex-1 bg-stone-100" />
        <span className="font-mono text-[11px] tabular-nums text-stone-500">
          {signed}/{total} signerade
        </span>
      </div>

      <div className="h-1 overflow-hidden rounded-full bg-stone-100">
        <div
          className={`regta-progress-fill h-full rounded-full ${
            signed === total && total > 0 ? 'bg-emerald-500' : 'bg-indigo-500'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <ul className="mt-0.5 flex flex-col divide-y divide-stone-100">
        {signers.map((s) => {
          const st = signerState(s);
          const ts = STATE_TEXT[st];
          const stamp = s.signedAt ?? s.seenAt;
          return (
            <li
              key={s.id}
              className="regta-track-row flex items-center gap-3 py-2"
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold ${NODE_STYLE[st]}`}
                aria-hidden="true"
              >
                {st === 'signed' ? (
                  <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                ) : st === 'viewed' ? (
                  <Eye className="h-3.5 w-3.5" />
                ) : (
                  initials(s.name)
                )}
              </span>
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-xs font-medium text-stone-800">
                  {s.name}
                </span>
                <span className="truncate text-[11px] text-stone-500">
                  {roleLabel(s.role)}
                  {s.capacity ? ` · ${s.capacity}` : ''}
                  {s.email ? ` · ${s.email}` : ''}
                </span>
              </div>
              <div className="flex shrink-0 flex-col items-end">
                <span className={`text-[11px] font-medium ${ts.cls}`}>
                  {ts.label}
                </span>
                {stamp && (
                  <span className="font-mono text-[10px] tabular-nums text-stone-400">
                    {formatSignedAt(stamp)}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── In-flight ───────────────────────────────────────────────────────

type SigningInfo = {
  dueAt: string | null;
  autoRemindAt: string | null;
  lastReminderSentAt: string | null;
};

/** Days from now until `iso` (UTC-day granularity); null if unparseable. */
function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / 86_400_000);
}

function InFlight({
  contract,
  signers,
  signingInfo,
  onCancelled,
}: {
  contract: ContractDetail;
  signers: SignerView[];
  signingInfo: SigningInfo;
  onCancelled: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<null | 'remind' | 'prolong' | 'cancel'>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [choosingCancel, setChoosingCancel] = useState(false);

  const run = async (which: 'remind' | 'prolong' | 'cancel', action: () => Promise<void>) => {
    setBusy(which);
    setErr(null);
    setNote(null);
    try {
      await action();
    } catch (e) {
      const anyErr = e as {
        data?: { reason?: string; next_allowed_at?: string };
        message?: string;
      };
      if (anyErr.data?.reason === 'reminder_rate_limited') {
        const next = anyErr.data.next_allowed_at;
        setErr(
          'Du kan skicka högst en påminnelse per dygn' +
            (next ? `. Nästa möjliga ${formatSignedAt(next)}.` : '.'),
        );
      } else {
        setErr(signingErrorText(e, 'Okänt fel'));
      }
    } finally {
      setBusy(null);
    }
  };

  const onCancel = (mode: CancelMode) => {
    setChoosingCancel(false);
    void run('cancel', async () => {
      if (mode === 'draft') await tenantApi.signing.return_to_draft({ contract_id: contract.id });
      else await tenantApi.signing.cancel({ contract_id: contract.id });
      await onCancelled();
    });
  };

  const onSendReminder = () =>
    void run('remind', async () => {
      await tenantApi.signing.send_reminder({ contract_id: contract.id });
      setNote('Påminnelse skickad till osignerade parter.');
      await onCancelled();
    });

  const onProlong = () => {
    const raw = prompt('Förläng signeringsfristen med hur många dagar?', '14');
    if (raw === null) return;
    const days = clampDays(raw, 14);
    void run('prolong', async () => {
      await tenantApi.signing.prolong({ contract_id: contract.id, days });
      setNote(`Fristen förlängd med ${days} dagar.`);
      await onCancelled();
    });
  };

  const dueDays = daysUntil(signingInfo.dueAt);
  const dueUrgency =
    dueDays === null
      ? 'text-stone-500'
      : dueDays < 0
        ? 'text-red-600'
        : dueDays <= 3
          ? 'text-amber-600'
          : 'text-stone-500';

  // Manual reminders are capped (see REMINDER_MIN_INTERVAL_MS). Disable
  // the button proactively when too soon; the backend enforces it too and
  // returns `reminder_rate_limited` if a stale client slips through.
  const lastRemindMs = signingInfo.lastReminderSentAt
    ? new Date(signingInfo.lastReminderSentAt).getTime()
    : null;
  const reminderTooSoon =
    lastRemindMs !== null && Date.now() - lastRemindMs < REMINDER_MIN_INTERVAL_MS;
  const nextReminderAt =
    lastRemindMs !== null ? new Date(lastRemindMs + REMINDER_MIN_INTERVAL_MS) : null;

  return (
    <div className="flex flex-col gap-4">
      <SignerTracker signers={signers} />

      {(signingInfo.dueAt || signingInfo.autoRemindAt || signingInfo.lastReminderSentAt) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-stone-100 pt-3">
          {signingInfo.dueAt && (
            <Meta icon={<Clock className="h-3 w-3" />} className={dueUrgency}>
              Förfaller {formatDueAt(signingInfo.dueAt)}
              {dueDays !== null && (
                <span className="ml-1 opacity-70">
                  {dueDays < 0
                    ? `(${Math.abs(dueDays)} d sedan)`
                    : dueDays === 0
                      ? '(idag)'
                      : `(${dueDays} d kvar)`}
                </span>
              )}
            </Meta>
          )}
          {signingInfo.autoRemindAt && (
            <Meta icon={<BellRing className="h-3 w-3" />}>
              Påminnelse {formatDueAt(signingInfo.autoRemindAt)}
            </Meta>
          )}
          {signingInfo.lastReminderSentAt && (
            <Meta icon={<Check className="h-3 w-3" />}>
              Påmind {formatDueAt(signingInfo.lastReminderSentAt)}
            </Meta>
          )}
        </div>
      )}

      {note && (
        <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          {note}
        </p>
      )}
      {err && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          {err}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-stone-100 pt-3">
        <button
          type="button"
          onClick={onSendReminder}
          disabled={busy !== null || reminderTooSoon}
          title={
            reminderTooSoon && nextReminderAt
              ? `Påminnelse skickad nyligen. Nästa möjliga ${formatSignedAt(nextReminderAt.toISOString())}.`
              : undefined
          }
          className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
        >
          <BellRing className="h-3.5 w-3.5" />
          {busy === 'remind' ? 'Skickar…' : 'Skicka påminnelse'}
        </button>
        <button
          type="button"
          onClick={onProlong}
          disabled={busy !== null}
          className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 transition-colors hover:bg-stone-50 disabled:opacity-50"
        >
          <CalendarClock className="h-3.5 w-3.5" />
          {busy === 'prolong' ? 'Förlänger…' : 'Förläng frist'}
        </button>
        <button
          type="button"
          onClick={() => setChoosingCancel(true)}
          disabled={busy !== null || choosingCancel}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
          {busy === 'cancel' ? 'Avbryter…' : 'Avbryt'}
        </button>
      </div>

      {choosingCancel && (
        <CancelSigningChoice
          workflow="scrive"
          onChoose={onCancel}
          onDismiss={() => setChoosingCancel(false)}
        />
      )}

      {contract.activeSigning?.scriveDocumentId && (
        <p className="font-mono text-[10px] tracking-wide text-stone-300">
          {contract.activeSigning.scriveDocumentId}
        </p>
      )}
    </div>
  );
}

function Meta({
  icon,
  className,
  children,
}: {
  icon: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] ${className ?? 'text-stone-500'}`}>
      <span className="text-stone-400">{icon}</span>
      <span className="font-mono tabular-nums">{children}</span>
    </span>
  );
}

// ─── Cancel: retry later vs. back to draft ───────────────────────────

type CancelMode = 'retry' | 'draft';

/**
 * Inline choice shown when the user clicks "Avbryt" on an in-flight
 * attempt (both workflows, both lanes): stop and keep the contract locked
 * as `cancelled` for a later resend, or stop and reopen it as an editable
 * draft (`signing.return_to_draft`).
 */
function CancelSigningChoice({
  workflow,
  onChoose,
  onDismiss,
}: {
  workflow: 'scrive' | 'manual';
  onChoose: (mode: CancelMode) => void;
  onDismiss: () => void;
}) {
  return (
    <div
      role="alertdialog"
      aria-label="Avbryt signeringen"
      className="flex flex-col gap-3 rounded-md border border-red-200 bg-red-50/60 p-3"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-stone-900">Avbryt signeringen?</p>
        <p className="text-xs text-stone-600">
          {workflow === 'scrive'
            ? 'Dokumentet återkallas hos Scrive och parterna kan inte längre signera det. Det går inte att ångra.'
            : 'Den utskrivna versionen gäller inte längre och kan inte laddas upp som signerad.'}
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => onChoose('draft')}
          className="flex flex-col items-start rounded-md border border-stone-300 bg-white px-3 py-2 text-left transition-colors hover:bg-stone-50"
        >
          <span className="text-xs font-medium text-stone-900">Avbryt och återgå till utkast</span>
          <span className="text-[11px] text-stone-500">
            Avtalet blir redigerbart igen. Påminnelser stoppas och ett nytt dokument skapas vid nästa
            utskick. Ändras avtalet bör det granskas på nytt innan det skickas.
          </span>
        </button>
        <button
          type="button"
          onClick={() => onChoose('retry')}
          className="flex flex-col items-start rounded-md border border-stone-300 bg-white px-3 py-2 text-left transition-colors hover:bg-stone-50"
        >
          <span className="text-xs font-medium text-stone-900">Avbryt, försök igen senare</span>
          <span className="text-[11px] text-stone-500">
            Avtalet förblir låst som avbrutet och kan skickas om utan ändringar.
          </span>
        </button>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="self-end rounded-md px-3 py-1.5 text-xs font-medium text-stone-600 transition-colors hover:bg-stone-100"
      >
        Fortsätt signeringen
      </button>
    </div>
  );
}

/**
 * "Återgå till utkast" for a `cancelled` contract — the
 * alternative to resending unchanged.
 */
function ReturnToDraftAction({
  contractId,
  onChanged,
}: {
  contractId: string;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const onClick = () => {
    if (
      !confirm(
        'Återgå till utkast? Avtalet blir redigerbart igen och ett nytt dokument skapas vid nästa utskick. Ändras avtalet bör det granskas på nytt innan det skickas.',
      )
    ) {
      return;
    }
    setBusy(true);
    setErr(null);
    void (async () => {
      try {
        await tenantApi.signing.return_to_draft({ contract_id: contractId });
        await onChanged();
      } catch (e) {
        setErr(signingErrorText(e, 'Kunde inte återgå till utkast'));
      } finally {
        setBusy(false);
      }
    })();
  };
  return (
    <div className="flex flex-col gap-2 border-b border-stone-100 pb-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs text-stone-600">Behöver avtalet ändras innan det skickas igen?</p>
        <button
          type="button"
          onClick={onClick}
          disabled={busy}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 transition-colors hover:bg-stone-50 disabled:opacity-50"
        >
          <FileText className="h-3.5 w-3.5" />
          {busy ? 'Återgår…' : 'Återgå till utkast'}
        </button>
      </div>
      {err && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{err}</p>
      )}
    </div>
  );
}

// ─── Signed: completion ──────────────────────────────────────────────

function Completed({
  contract,
  signers,
}: {
  contract: ContractDetail;
  signers: SignerView[];
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50/70 px-3 py-2.5">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        <div className="text-xs text-emerald-900">
          <div className="font-medium">Alla parter har signerat.</div>
          <div className="mt-0.5 text-emerald-800/80">
            Den signerade PDF:en når du via “Visa slutgiltig PDF” ovan.
          </div>
        </div>
      </div>
      <SignerTracker signers={signers} />
      {contract.activeSigning?.scriveDocumentId && (
        <p className="font-mono text-[10px] tracking-wide text-stone-300">
          {contract.activeSigning.scriveDocumentId}
        </p>
      )}
    </div>
  );
}

// ─── Manual: in-flight (download → sign offline → upload) ────────────

function DownloadBtn({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 transition-colors hover:bg-stone-50"
    >
      <Download className="h-3.5 w-3.5" />
      {children}
    </button>
  );
}

function ManualInFlight({
  contract,
  signers,
  onChanged,
}: {
  contract: ContractDetail;
  signers: SignerView[];
  onChanged: () => Promise<void>;
}) {
  const stateQ = useTenantQuery(
    'signing.manual_state',
    (cid) => tenantApi.signing.manual_state({ contract_id: cid as string }),
    [contract.id],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const bilagor = contract.attachments ?? [];
  // Multi-document templates (per ADR-0057) expose each document as its own
  // download. `templateDocumentParts` returns a one-element list for a
  // single-document template, so `multiDoc` gates the extra buttons.
  const tmpl = getTemplateVersion(contract.templateKey, contract.templateVersion);
  const docParts = tmpl ? templateDocumentParts(tmpl) : [];
  const multiDoc = docParts.length > 1;
  const [busy, setBusy] = useState<null | 'cancel' | 'complete'>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [choosingCancel, setChoosingCancel] = useState(false);

  const downloadPart = async (
    part?:
      | { part: 'head' }
      | { part: 'bilaga'; n: number }
      | { part: 'doc'; key: string },
  ) => {
    const blob = await tenantApi.contracts.render(contract.id, part);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const onComplete = async () => {
    if (files.length === 0) return;
    setBusy('complete');
    setErr(null);
    try {
      const documents: Array<{ upload_key: string; filename: string; mime: string; bytes: number }> = [];
      for (const f of files) {
        const mime = f.type || 'application/pdf';
        const { upload_url, upload_key } = await tenantApi.signing.signed_upload_url({
          contract_id: contract.id,
          filename: f.name,
          mime,
          bytes: f.size,
        });
        const put = await fetch(upload_url, {
          method: 'PUT',
          headers: { 'content-type': mime },
          body: f,
        });
        if (!put.ok) throw new Error('upload_failed');
        documents.push({ upload_key, filename: f.name, mime, bytes: f.size });
      }
      await tenantApi.signing.complete_manual({ contract_id: contract.id, documents });
      await onChanged();
    } catch (e) {
      const anyErr = e as { data?: { reason?: string }; message?: string };
      setErr(anyErr.data?.reason ?? anyErr.message ?? 'Kunde inte slutföra');
    } finally {
      setBusy(null);
    }
  };

  const onCancel = (mode: CancelMode) => {
    setChoosingCancel(false);
    setBusy('cancel');
    setErr(null);
    void (async () => {
      try {
        if (mode === 'draft') await tenantApi.signing.return_to_draft({ contract_id: contract.id });
        else await tenantApi.signing.cancel_manual({ contract_id: contract.id });
        await onChanged();
      } catch (e) {
        setErr((e as { message?: string }).message ?? 'Fel');
      } finally {
        setBusy(null);
      }
    })();
  };

  const dueAt =
    stateQ.data?.signing?.signing_due_at ?? contract.activeSigning?.signingDueAt ?? null;

  return (
    <div className="flex flex-col gap-4">
      <SignerTracker signers={signers} />

      <div className="flex flex-col gap-2 border-t border-stone-100 pt-3">
        <SectionLabel>Ladda ner för signering</SectionLabel>
        <div className="flex flex-wrap gap-2">
          <DownloadBtn onClick={() => void downloadPart()}>Sammanslaget (PDF)</DownloadBtn>
          {/* Multi-document template (ADR-0057): one button per document. */}
          {multiDoc &&
            docParts.map((d) => (
              <DownloadBtn key={d.key} onClick={() => void downloadPart({ part: 'doc', key: d.key })}>
                {d.title}
              </DownloadBtn>
            ))}
          {bilagor.length > 0 && (
            <DownloadBtn onClick={() => void downloadPart({ part: 'head' })}>
              Huvuddokument
            </DownloadBtn>
          )}
          {bilagor.map((b, i) => (
            <DownloadBtn key={b.id} onClick={() => void downloadPart({ part: 'bilaga', n: i })}>
              {`Bilaga ${b.number ?? i + 1}`}
            </DownloadBtn>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2 border-t border-stone-100 pt-3">
        <SectionLabel>Ladda upp signerade dokument</SectionLabel>
        <input
          type="file"
          accept="application/pdf"
          multiple
          onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          className="text-xs text-stone-600 file:mr-3 file:rounded-md file:border-0 file:bg-stone-100 file:px-3 file:py-1.5 file:text-xs file:font-medium"
        />
        {err && (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            {err}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void onComplete()}
            disabled={busy !== null || files.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
          >
            <Upload className="h-3.5 w-3.5" />
            {busy === 'complete' ? 'Slutför…' : 'Markera som signerat'}
          </button>
          <button
            type="button"
            onClick={() => setChoosingCancel(true)}
            disabled={busy !== null || choosingCancel}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
            {busy === 'cancel' ? 'Avbryter…' : 'Avbryt'}
          </button>
        </div>
        {choosingCancel && (
          <CancelSigningChoice
            workflow="manual"
            onChoose={onCancel}
            onDismiss={() => setChoosingCancel(false)}
          />
        )}
      </div>

      {dueAt && (
        <div className="border-t border-stone-100 pt-3">
          <Meta icon={<Clock className="h-3 w-3" />}>Påminnelse {formatDueAt(dueAt)}</Meta>
        </div>
      )}
    </div>
  );
}

function ManualCompleted({
  contract,
  signers,
}: {
  contract: ContractDetail;
  signers: SignerView[];
}) {
  const stateQ = useTenantQuery(
    'signing.manual_state',
    (cid) => tenantApi.signing.manual_state({ contract_id: cid as string }),
    [contract.id],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const docs = stateQ.data?.signed_documents ?? [];
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50/70 px-3 py-2.5">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        <div className="text-xs text-emerald-900">
          <div className="font-medium">Avtalet är signerat manuellt.</div>
          <div className="mt-0.5 text-emerald-800/80">
            De uppladdade signerade dokumenten finns nedan.
          </div>
        </div>
      </div>
      <SignerTracker signers={signers} />
      {docs.length > 0 && (
        <div className="flex flex-col gap-2 border-t border-stone-100 pt-3">
          <SectionLabel>Signerade dokument</SectionLabel>
          <ul className="flex flex-col gap-1">
            {docs.map((d) => (
              <li key={d.id}>
                <a
                  href={d.download_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-indigo-700 hover:underline"
                >
                  <Download className="h-3.5 w-3.5" />
                  {d.label ?? d.filename}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Pre-send: method chooser → Scrive or Manual ─────────────────────

function MethodButton({
  active,
  onClick,
  title,
  desc,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-md border px-3 py-2.5 text-left transition-colors ${
        active
          ? 'border-purple-400 bg-purple-50'
          : 'border-stone-200 bg-white hover:border-stone-300'
      }`}
    >
      <div className="text-xs font-semibold text-stone-800">{title}</div>
      <div className="mt-0.5 text-[11px] leading-snug text-stone-500">{desc}</div>
    </button>
  );
}

function SigningMethodGate({
  token,
  contractId,
  signers,
  onChanged,
  resend = false,
  lastScriveDocumentId = null,
  parent = null,
}: {
  token: string;
  contractId: string;
  signers: ContractParty[];
  onChanged: () => Promise<void>;
  resend?: boolean;
  lastScriveDocumentId?: string | null;
  parent?: { id: string; title: string } | null;
}) {
  const readinessQ = useTenantQuery(
    'signing.readiness',
    (cid) => tenantApi.signing.readiness({ contract_id: cid as string }),
    [contractId],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const r = readinessQ.data;
  const available = r?.available_methods ?? ['scrive', 'manual'];
  const forced = r?.forced_method ?? null;
  const [picked, setPicked] = useState<SigningMethod | null>(null);
  // ADR-0100: the method planned in the draft is the default choice.
  const plannedMethod = useSigningDraft(contractId).data?.resolved.method ?? null;
  const planned = plannedMethod && available.includes(plannedMethod) ? plannedMethod : null;
  const chosen: SigningMethod | null =
    forced ?? picked ?? planned ?? (available.length === 1 ? available[0]! : 'scrive');

  return (
    <div className="flex flex-col gap-4">
      {!forced && available.length > 1 && (
        <div className="flex flex-col gap-2">
          <SectionLabel>Signeringssätt</SectionLabel>
          <div className="flex flex-wrap gap-2">
            <MethodButton
              active={chosen === 'scrive'}
              onClick={() => setPicked('scrive')}
              title="E-signering (Scrive)"
              desc="Skicka för digital signering med BankID."
            />
            <MethodButton
              active={chosen === 'manual'}
              onClick={() => setPicked('manual')}
              title="Manuell signering"
              desc="Exportera, signera för hand, ladda upp."
            />
          </div>
        </div>
      )}
      {forced === 'manual' && (
        <p className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-[11px] text-stone-600">
          Den här avtalstypen signeras manuellt — ingen e-signering.
        </p>
      )}
      {!forced && available.length === 1 && available[0] === 'manual' && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
          E-signering (Scrive) är inte aktiverat ännu för er organisation. Du kan signera
          manuellt under tiden.
        </p>
      )}

      {chosen === 'scrive' && (
        <SendForSigning
          token={token}
          contractId={contractId}
          signers={signers}
          onSent={onChanged}
          resend={resend}
          lastScriveDocumentId={lastScriveDocumentId}
          parent={parent}
        />
      )}
      {chosen === 'manual' && (
        <ManualPrepare
          contractId={contractId}
          signers={signers}
          onPrepared={onChanged}
          resend={resend}
          parent={parent}
        />
      )}
    </div>
  );
}

// ─── Manual: prepare (render + freeze + await offline signing) ────────

function ManualPrepare({
  contractId,
  signers,
  onPrepared,
  resend = false,
  parent = null,
}: {
  contractId: string;
  signers: ContractParty[];
  onPrepared: () => Promise<void>;
  resend?: boolean;
  parent?: { id: string; title: string } | null;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [monitoringDisabled, setMonitoringDisabled] = useState(false);
  const [daysToSign, setDaysToSign] = useState(30);
  // ADR-0100: start from the signing plan's options (once, when it loads).
  const manualPlan = useSigningDraft(contractId).data;
  const [manualSeeded, setManualSeeded] = useState(false);
  useEffect(() => {
    if (!manualPlan || manualSeeded) return;
    setManualSeeded(true);
    const o = manualPlan.options;
    if (o.monitoring_disabled !== undefined) setMonitoringDisabled(o.monitoring_disabled);
    if (o.days_to_sign !== undefined) setDaysToSign(o.days_to_sign);
    if (o.include_place_date !== undefined) setIncludeOrtDatum(o.include_place_date);
  }, [manualPlan, manualSeeded]);
  // Manual defaults to paper signing (statement + Ort/Datum on); off leaves
  // the document unadapted for signing outside Regta.
  const [includeOrtDatum, setIncludeOrtDatum] = useState(true);
  const [rosterPayload, setRosterPayload] = useState<RosterPayload | null>(null);
  const [irDiagnostics, setIrDiagnostics] = useState<IrDiagnostic[]>([]);
  const noParties = signers.length === 0;

  const prepare = async () => {
    setBusy(true);
    setErr(null);
    setIrDiagnostics([]);
    try {
      if (rosterPayload) {
        await tenantApi.signing.set_signers({ contract_id: contractId, signers: rosterPayload });
      }
      await tenantApi.signing.prepare_manual({
        contract_id: contractId,
        monitoring_disabled: monitoringDisabled,
        days_to_sign: daysToSign,
        include_ort_datum: includeOrtDatum,
      });
      await onPrepared();
    } catch (e) {
      setErr(signingErrorText(e, 'Okänt fel'));
      setIrDiagnostics(irDiagnosticsOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <SectionLabel>Undertecknare</SectionLabel>
        <p className="text-[11px] leading-relaxed text-stone-500">
          {noParties
            ? 'Lägg till parter på avtalet innan du förbereder för signering.'
            : 'Namnen fylls i underskriftsblocket. E-post och BankID behövs inte för manuell signering.'}
        </p>
        <SignerRosterEditor
          contractId={contractId}
          method="manual"
          onSaved={onPrepared}
          onRosterChange={setRosterPayload}
          parent={parent}
        />
      </div>

      <div className="flex flex-col gap-3 border-t border-stone-100 pt-3">
        <SectionLabel>Bevakning</SectionLabel>
        <label className="flex items-center gap-2 text-xs text-stone-600">
          <span>Påminn om signering inom</span>
          <input
            type="number"
            min={1}
            max={90}
            value={daysToSign}
            disabled={monitoringDisabled}
            onChange={(e) => setDaysToSign(clampDays(e.target.value, 30))}
            className="w-14 rounded-md border border-stone-300 px-2 py-1 text-center font-mono text-xs tabular-nums disabled:opacity-40"
          />
          <span className="text-stone-400">dagar</span>
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-[11px] text-stone-600">
          <input
            type="checkbox"
            checked={monitoringDisabled}
            onChange={(e) => setMonitoringDisabled(e.target.checked)}
            className="accent-indigo-600"
          />
          <span>Bevaka inte detta avtal (inga påminnelser)</span>
        </label>
        <SigningStatementToggle
          checked={includeOrtDatum}
          onChange={setIncludeOrtDatum}
          method="manual"
        />
      </div>

      {err && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          {err}
        </p>
      )}
      <IrSigningDiagnostics diagnostics={irDiagnostics} />
      <button
        type="button"
        onClick={() => void prepare()}
        disabled={busy || noParties}
        className="inline-flex items-center justify-center gap-2 self-start rounded-md bg-purple-700 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-purple-800 disabled:opacity-50"
      >
        <FileText className="h-3.5 w-3.5" />
        {busy ? 'Förbereder…' : resend ? 'Förbered igen' : 'Förbered för signering'}
      </button>
    </div>
  );
}

// ─── Pre-send: roster + settings + "Skicka för signering" ────────────

function SendForSigning({
  contractId,
  signers,
  onSent,
  resend = false,
  lastScriveDocumentId = null,
  parent = null,
}: {
  token: string;
  contractId: string;
  signers: ContractParty[];
  onSent: () => Promise<void>;
  /** True when re-launching signing from a `cancelled` contract. */
  resend?: boolean;
  /** Scrive document id of the previous (terminal) attempt, if any. */
  lastScriveDocumentId?: string | null;
  parent?: { id: string; title: string } | null;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<PreflightError | null>(null);
  // Signing statement + coupled Ort/Datum rows. Off by default for the
  // Scrive (digital) flow → digital statement, no place/date blanks
  // (BankID stamps the signing date into the watermark). Opt in for two
  // physical originals signed by hand.
  const [includeOrtDatum, setIncludeOrtDatum] = useState(false);
  // Signing deadline (Scrive days_to_sign) + automatic-reminder cadence
  // (days_to_remind). Defaults mirror the backend (30 / 7). The reminder
  // must fall before the deadline; the input is clamped on send.
  const [daysToSign, setDaysToSign] = useState(30);
  const [remindEnabled, setRemindEnabled] = useState(true);
  const [daysToRemind, setDaysToRemind] = useState(7);
  // ADR-0100: start from the signing plan's options (once, when it loads).
  const [signingOrder, setSigningOrder] = useState<'parallel' | 'sequential'>('parallel');
  const sendPlan = useSigningDraft(contractId).data;
  const [sendSeeded, setSendSeeded] = useState(false);
  useEffect(() => {
    if (!sendPlan || sendSeeded) return;
    setSendSeeded(true);
    const o = sendPlan.options;
    if (o.include_place_date !== undefined) setIncludeOrtDatum(o.include_place_date);
    if (o.days_to_sign !== undefined) setDaysToSign(o.days_to_sign);
    if (o.days_to_remind === null) setRemindEnabled(false);
    else if (o.days_to_remind !== undefined) setDaysToRemind(o.days_to_remind);
    if (o.signing_order) setSigningOrder(o.signing_order);
  }, [sendPlan, sendSeeded]);
  // Latest roster payload published by the editor — persisted right
  // before send so the on-screen state is authoritative (not the last
  // explicit "Spara").
  const [rosterPayload, setRosterPayload] = useState<RosterPayload | null>(null);

  // Server-side readiness check (per ADR-0032). Tells us whether the
  // tenant's Scrive customer account is provisioned. We can't compute
  // this client-side — only the admin-backend knows.
  const readinessQ = useTenantQuery(
    'signing.readiness',
    (cid) => tenantApi.signing.readiness({ contract_id: cid as string }),
    [contractId],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const serverReadiness: PreflightError | null = (() => {
    const r = readinessQ.data;
    if (!r) return null;
    // This component drives the Scrive path; if the tenant has no Scrive
    // account, that path is blocked (manual is offered separately).
    if (!r.scrive_provisioned) {
      return { reason: 'scrive_account_not_provisioned' };
    }
    // SE BankID gate (ADR-0032 Phase B): a BankID signature can't go out
    // until the tenant's branded display-name order is active in Scrive.
    // Whether BankID is actually used is read from the LIVE roster so the
    // block tracks unsaved edits — a default (un-edited) roster signs with
    // BankID, so treat "no roster published yet" as BankID-present.
    const bankidSignerLive = rosterPayload
      ? rosterPayload.some((s) => s.auth_method === 'se_bankid')
      : true;
    if (bankidSignerLive && r.bankid_order_state !== 'active') {
      return {
        reason: 'scrive_bankid_not_active',
        orderState: r.bankid_order_state,
        orderedAt: r.bankid_ordered_at,
      };
    }
    // Other reasons are covered by the client-side preflight below; let
    // it win for snappier feedback.
    return null;
  })();

  // Client-side pre-flight echoes the backend's. Lets the user see
  // what's blocking before pressing the button. Once the editor has
  // published a roster we validate THAT (signer emails + BankID
  // personnummer) rather than the party rows — an org party's email may
  // be blank while its signers each have one.
  const clientPreflight: PreflightError | null = (() => {
    if (signers.length === 0) return { reason: 'no_signing_parties' };
    if (rosterPayload && rosterPayload.length > 0) {
      const describe = (s: RosterPayload[number]): MissingSigner => {
        const p = signers.find((sp) => sp.id === s.party_id);
        return {
          party_id: s.party_id,
          role: p?.role ?? 'lessee',
          ordinal: p?.ordinal ?? 0,
          name: s.legal_name,
        };
      };
      const noEmail = rosterPayload.filter((s) => !s.email?.trim()).map(describe);
      if (noEmail.length > 0) return { reason: 'missing_party_emails', missing: noEmail };
      const bankidNoPn = rosterPayload
        .filter((s) => s.auth_method === 'se_bankid' && !looksLikePersonnummer(s.personal_number))
        .map(describe);
      if (bankidNoPn.length > 0) {
        return { reason: 'missing_signer_personal_number', missing: bankidNoPn };
      }
      return null;
    }
    const missing = signers
      .filter((s) => !s.email?.trim())
      .map((s) => ({
        party_id: s.id,
        role: s.role,
        ordinal: s.ordinal,
        name: s.legalName,
      }));
    if (missing.length > 0) return { reason: 'missing_party_emails', missing };
    return null;
  })();

  // Server-reported state takes priority — a tenant without a Scrive
  // account can't be unblocked by editing parties.
  const blocking = err ?? serverReadiness ?? clientPreflight;
  const canSend = !blocking && !busy;
  const remindTooLate = remindEnabled && daysToRemind >= daysToSign;

  const send = async () => {
    setBusy(true);
    setErr(null);
    try {
      // Flush the editor's current roster first so we send exactly what's
      // on screen, then start signing.
      if (rosterPayload) {
        await tenantApi.signing.set_signers({
          contract_id: contractId,
          signers: rosterPayload,
        });
      }
      const remind = remindEnabled && daysToRemind < daysToSign ? daysToRemind : null;
      await tenantApi.signing.send_for_signing({
        contract_id: contractId,
        include_ort_datum: includeOrtDatum,
        days_to_sign: daysToSign,
        days_to_remind: remind,
        signing_order: signingOrder,
      });
      await onSent();
    } catch (e) {
      const anyErr = e as {
        data?: { reason?: string; missing?: unknown; bankid_order_state?: BankIdOrderState };
        message?: string;
      };
      const reason = anyErr.data?.reason;
      if (reason === 'no_signing_parties') {
        setErr({ reason: 'no_signing_parties' });
      } else if (reason === 'missing_party_emails') {
        setErr({
          reason: 'missing_party_emails',
          missing: (anyErr.data?.missing as MissingSigner[]) ?? [],
        });
      } else if (reason === 'missing_signer_personal_number') {
        setErr({
          reason: 'missing_signer_personal_number',
          missing: (anyErr.data?.missing as MissingSigner[]) ?? [],
        });
      } else if (reason === 'scrive_account_not_provisioned') {
        setErr({ reason: 'scrive_account_not_provisioned' });
      } else if (reason === 'scrive_bankid_not_active') {
        setErr({
          reason: 'scrive_bankid_not_active',
          orderState: anyErr.data?.bankid_order_state ?? 'not_ordered',
          orderedAt: null,
        });
      } else if (reason === 'review_required_for_deviation') {
        setErr({ reason: 'review_required_for_deviation', message: signingErrorText(e, 'Okänt fel') });
      } else if (reason === 'ir_document_has_errors') {
        setErr({
          reason: 'ir_document_has_errors',
          message: signingErrorText(e, 'Okänt fel'),
          diagnostics: irDiagnosticsOf(e),
        });
      } else {
        setErr({ reason: 'other', message: signingErrorText(e, 'Okänt fel') });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {resend && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
          <div className="font-medium">Föregående signering avbröts.</div>
          <div className="mt-0.5 text-amber-800/80">
            En ny signering hos Scrive startas när du skickar igen.
            {lastScriveDocumentId && (
              <span className="ml-1">Tidigare id: {lastScriveDocumentId.slice(-8)}.</span>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <SectionLabel>Undertecknare</SectionLabel>
        <p className="text-[11px] leading-relaxed text-stone-500">
          {signers.length === 0
            ? 'Lägg till parter på avtalet innan du anger signerare.'
            : 'Varje part kan signeras av en eller flera personer (t.ex. firmatecknare). BankID är standard.'}
        </p>
        <SignerRosterEditor
          contractId={contractId}
          onSaved={onSent}
          onRosterChange={setRosterPayload}
          parent={parent}
        />
      </div>

      <div className="flex flex-col gap-3 border-t border-stone-100 pt-3">
        <SectionLabel>Tidsfrist &amp; påminnelser</SectionLabel>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          <label className="flex items-center gap-2 text-xs text-stone-600">
            <span>Signeras inom</span>
            <input
              type="number"
              min={1}
              max={90}
              value={daysToSign}
              onChange={(e) => setDaysToSign(clampDays(e.target.value, 30))}
              className="w-14 rounded-md border border-stone-300 px-2 py-1 text-center font-mono text-xs tabular-nums"
            />
            <span className="text-stone-400">dagar</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-stone-600">
            <input
              type="checkbox"
              checked={remindEnabled}
              onChange={(e) => setRemindEnabled(e.target.checked)}
              className="accent-indigo-600"
            />
            <span>Auto-påminnelse efter</span>
            <input
              type="number"
              min={1}
              max={Math.max(1, daysToSign - 1)}
              value={daysToRemind}
              disabled={!remindEnabled}
              onChange={(e) => setDaysToRemind(clampDays(e.target.value, 7))}
              className="w-14 rounded-md border border-stone-300 px-2 py-1 text-center font-mono text-xs tabular-nums disabled:opacity-40"
            />
            <span className="text-stone-400">dagar</span>
          </label>
        </div>
        <p className="text-[11px] leading-relaxed text-stone-500">
          Scrive mejlar osignerade parter automatiskt; appen bevakar fristen åt dig.
          {remindTooLate && (
            <span className="mt-0.5 block text-amber-700">
              Påminnelsen måste ligga före fristen — den skickas inte med nuvarande värden.
            </span>
          )}
        </p>
        <label className="flex items-center gap-2 text-xs text-stone-600">
          <span>Signeringsordning</span>
          <select
            value={signingOrder}
            onChange={(e) => setSigningOrder(e.target.value as 'parallel' | 'sequential')}
            className="rounded-md border border-stone-300 px-1.5 py-1 text-xs"
          >
            <option value="parallel">Alla bjuds in samtidigt</option>
            <option value="sequential">En i taget, i listans ordning</option>
          </select>
        </label>
        <SigningStatementToggle
          checked={includeOrtDatum}
          onChange={setIncludeOrtDatum}
          method="scrive"
        />
      </div>

      {blocking && <BlockingPreflight err={blocking} />}

      <button
        type="button"
        onClick={() => void send()}
        disabled={!canSend}
        className="inline-flex items-center justify-center gap-2 self-start rounded-md bg-purple-700 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-purple-800 disabled:opacity-50"
      >
        <Send className="h-3.5 w-3.5" />
        {busy ? 'Skickar…' : resend ? 'Skicka igen' : 'Skicka för signering'}
      </button>
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-400">
      {children}
    </span>
  );
}

/**
 * One toggle that adapts the document for paper signing: the "var sitt
 * original" statement above the signatures plus the Ort/Datum blanks
 * (backend `include_ort_datum`). Checked = paper originals signed by hand.
 * Unchecked differs per method: Scrive prints the digital statement (date
 * from the watermark); manual prints no statement at all, leaving the
 * document unadapted for signing outside Regta (e.g. the tenant's own
 * e-signing service). Default off for Scrive, on for manual.
 */
function SigningStatementToggle({
  checked,
  onChange,
  method,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  method: SigningMethod;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2 text-[11px] text-stone-600">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 accent-indigo-600"
      />
      {method === 'manual' ? (
        <span>
          <span className="font-medium text-stone-700">
            Anpassa för pappersunderskrift
          </span>{' '}
          — lägg till ort och datum vid varje underskrift samt att parterna
          tagit var sitt original. Lämna omarkerad om ni signerar på annat
          sätt, t.ex. med en egen e-signeringstjänst.
        </span>
      ) : (
        <span>
          <span className="font-medium text-stone-700">
            Anpassas för utskrift
          </span>{' '}
          — lägg till ort och datum vid varje underskrift. Lämna omarkerad för
          digital signering, där signeringsdatum framgår av vattenstämpeln.
        </span>
      )}
    </label>
  );
}

function BlockingPreflight({ err }: { err: PreflightError }) {
  if (err.reason === 'scrive_account_not_provisioned') {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
        <div className="font-medium">
          Signering är ännu inte aktiverad för er organisation.
        </div>
        <div className="mt-0.5 text-amber-800/80">
          Vår e-signeringsleverantör (Scrive) håller på att slutföra uppsättningen.
          Er administratör får besked när det är klart. Avtalet ligger kvar som
          utkast tills dess.
        </div>
      </div>
    );
  }
  if (err.reason === 'scrive_bankid_not_active') {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-xs text-amber-900">
        <div className="font-medium">
          BankID-signering är inte aktiverad för er organisation ännu.
        </div>
        <div className="mt-1 text-amber-800/80">
          {bankidProgressText(err.orderState, err.orderedAt)}
        </div>
        <div className="mt-2 rounded border border-amber-200 bg-amber-100/60 px-2 py-1.5 text-amber-900">
          <span className="font-medium">Status på beställningen:</span>{' '}
          {bankidStateLabel(err.orderState)}
        </div>
        <div className="mt-2 text-amber-800/80">
          Vi saknar i nuläget automatiska aviseringar för det här steget. Hör av
          er till{' '}
          <a
            href="mailto:support@regta.se"
            className="font-medium text-amber-900 underline"
          >
            support@regta.se
          </a>{' '}
          så hjälper vi er att slutföra eller kontrollera beställningen.
        </div>
        <div className="mt-2 border-t border-amber-200 pt-2">
          <span className="font-medium">
            Att signera utan BankID, där BankID normalt används, rekommenderas
            inte.
          </span>{' '}
          Vill ni ändå gå vidare nu kan ni välja ”Utan BankID” för
          undertecknarna ovan, eller använda manuell signering under tiden.
        </div>
      </div>
    );
  }
  if (err.reason === 'no_signing_parties') {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
        Avtalet saknar signerare. Lägg till hyresvärd och hyresgäst innan du skickar.
      </div>
    );
  }
  if (err.reason === 'missing_party_emails') {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
        <div className="font-medium">E-post saknas för:</div>
        <ul className="mt-1 list-disc pl-4">
          {err.missing.map((m, i) => (
            <li key={m.signer_id ?? `${m.party_id}-${i}`}>
              {m.name} ({roleLabel(m.role)})
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (err.reason === 'missing_signer_personal_number') {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
        <div className="font-medium">Personnummer saknas för BankID-signering:</div>
        <ul className="mt-1 list-disc pl-4">
          {err.missing.map((m, i) => (
            <li key={m.signer_id ?? `${m.party_id}-${i}`}>
              {m.name} ({roleLabel(m.role)})
            </li>
          ))}
        </ul>
        <div className="mt-1.5 text-amber-800/80">
          Ange personnummer, eller välj ”Utan BankID” för den undertecknaren.
        </div>
      </div>
    );
  }
  if (err.reason === 'review_required_for_deviation') {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
        <div className="font-medium">Granskning krävs</div>
        <div className="mt-1">{err.message}</div>
      </div>
    );
  }
  if (err.reason === 'ir_document_has_errors') {
    return (
      <div className="flex flex-col gap-2">
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2.5 text-xs text-red-900">
          {err.message}
        </div>
        <IrSigningDiagnostics diagnostics={err.diagnostics} />
      </div>
    );
  }
  return (
    <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2.5 text-xs text-red-900">
      Kunde inte skicka för signering: {err.message}
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────

/** Short status chip for the tenant's SE BankID display-name order. */
function bankidStateLabel(state: BankIdOrderState): string {
  switch (state) {
    case 'not_ordered':
      return 'Inte beställd';
    case 'order_submitted':
      return 'Inskickad — inväntar aktivering';
    case 'rejected':
      return 'Avvisad — behöver göras om';
    case 'active':
      return 'Aktiv';
  }
}

/** Plain-Swedish explanation of where the BankID order stands. */
function bankidProgressText(
  state: BankIdOrderState,
  orderedAt: string | null,
): string {
  switch (state) {
    case 'not_ordered':
      return 'Er BankID-avsändare är ännu inte beställd hos vår signeringsleverantör (Scrive). Tills den är aktiv kan BankID-signering inte skickas.';
    case 'order_submitted':
      return orderedAt
        ? `Beställningen skickades in ${formatDueAt(orderedAt)} och inväntar aktivering hos Scrive. Det här brukar ta några arbetsdagar.`
        : 'Beställningen är inskickad och inväntar aktivering hos Scrive. Det här brukar ta några arbetsdagar.';
    case 'rejected':
      return 'Beställningen av er BankID-avsändare avvisades av Scrive och behöver göras om innan BankID-signering kan användas.';
    case 'active':
      return 'BankID är aktivt.';
  }
}

function roleLabel(role: string): string {
  switch (role) {
    case 'lessor':
      return 'Hyresvärd';
    case 'lessee':
      return 'Hyresgäst';
    case 'guarantor':
      return 'Borgensman';
    case 'witness':
      return 'Vittne';
    default:
      return role;
  }
}

function formatSignedAt(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString('sv-SE')} ${d.toLocaleTimeString('sv-SE', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  } catch {
    return iso;
  }
}

/** Date-only label for the signing deadline / reminder timestamps. */
function formatDueAt(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('sv-SE');
  } catch {
    return iso;
  }
}

/** Parse a day-count input, clamp to 1..90, fall back on garbage. */
function clampDays(raw: string, fallback: number): number {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(90, Math.max(1, n));
}
